import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize';
import { HighlightedPhrase } from '@openx/components-core/lib/HighlightedPhrase/HighlightedPhrase';
import type { DealInsightsDealEntryApi } from '@openx/types/dealInsights';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type DealNameOrIdAutocompleteProps = {
  name: string;
  options: DealInsightsDealEntryApi[];
  disabled?: boolean;
  loading?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
};

const AUTOCOMPLETE_OPTION_HEIGHT = 80;

const OptionRow = React.memo(function OptionRow({
  searchPhrase,
  dealId,
  impressions,
  labelDealId,
  labelImpr,
}: {
  searchPhrase: string;
  dealId: string;
  impressions: number | null;
  labelDealId: string;
  labelImpr: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {labelDealId}:{' '}
        <Typography variant="body1Bold" component="span" sx={{ wordBreak: 'break-all', whiteSpace: 'normal' }}>
          <HighlightedPhrase searchPhrase={searchPhrase}>{dealId}</HighlightedPhrase>
        </Typography>
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {labelImpr}:{' '}
        <Typography variant="body1Bold" component="span">
          {abbreviateNumericValue(impressions, { allowZero: true, nullValue: 'N/A' })}
        </Typography>
      </Typography>
    </Box>
  );
});

export const DealNameOrIdAutocomplete = ({
  name,
  options,
  disabled,
  loading,
  onOpen,
  onClose,
}: DealNameOrIdAutocompleteProps) => {
  const { t } = useTranslation();

  const [searchPhrase, setSearchPhrase] = useState('');
  const labelDealId = useMemo(() => t('Deal ID'), [t]);
  const labelImpr = useMemo(() => t('Impr.'), [t]);

  return (
    <RHFAutoCompleteVirtualize
      name={name}
      disabled={disabled}
      textFieldProps={{
        label: 'Deal Name or ID',
        margin: 'dense',
        placeholder: t('Select or search'),
      }}
      multiple
      onOpen={onOpen}
      onClose={onClose}
      loading={loading}
      filterOptions={(options, state) =>
        options.filter(
          option =>
            option.name.toLowerCase().includes(state.inputValue.toLowerCase()) ||
            option.deal_id.toLowerCase().includes(state.inputValue.toLowerCase())
        )
      }
      fullWidth
      options={options}
      getOptionLabel={option => option.name}
      getOptionKey={option => option.name}
      data-test="deal-name-or-id"
      itemSize={AUTOCOMPLETE_OPTION_HEIGHT}
      sx={{
        '& .MuiAutocomplete-inputRoot': {
          maxHeight: '200px',
          overflowY: 'auto',
        },
      }}
      isOptionEqualToValue={(option, value) => {
        if (typeof value === 'string') return false;
        return option.name === value.name;
      }}
      slotProps={{
        popper: {
          sx: {
            '& .MuiAutocomplete-option': {
              height: 'auto',
              padding: '0px 8px !important',
            },
            '& .MuiIconButton-root': {
              margin: 'auto 0',
            },
          },
        },
      }}
      onInputChange={(_, value) => setSearchPhrase(value)}
      renderOptions={{
        renderChildren: option => (
          <OptionRow
            labelDealId={labelDealId}
            labelImpr={labelImpr}
            searchPhrase={searchPhrase}
            dealId={option.deal_id}
            impressions={option.impressions}
          />
        ),
      }}
    />
  );
};
